import { useRefCallback } from "@enfusion-ui/hooks";
import { getFileIcon, getFileName } from "@enfusion-ui/utils";
import { AppLogging, getRootNode, getSearchRootNode, retrieveNodes, styled, useAuth, } from "@enfusion-ui/web-core";
import { debounce } from "lodash";
import * as React from "react";
import { Spinner } from "../display/Spinner";
import { FileExplorer } from "../files/FileExplorer/FileExplorer";
import { Portal } from "../portal/Portal";
import { CenterContent } from "../Styled";
import { TextInput } from "./TextInput";
const ROOT_USER = "user";
const ROOT_SHARED = "shared";
const ROOT_GLOBAL = "global";
const ROOT_FIXED = "fixed";
const OptionsContainer = styled.div `
  position: relative;
  overflow-y: auto;
  overflow-x: hidden;
  background-color: var(--background-color-1);
  max-height: 150px;
`;
const SingleRowContainer = styled.div `
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-column-gap: var(--spacing-l);
`;
const getNodes = retrieveNodes("reports");
function createRootFolder(name, nodes, path) {
    if (nodes.length > 0)
        return {
            id: `root-${path}`,
            name,
            path,
            file: false,
            nodes,
        };
    return null;
}
const fixedReportList = [
    {
        id: "pnl",
        name: "Fixed P&L",
        path: "fixed/pnl",
        pathParams: {
            groupingType: null,
        },
        file: true,
    },
    {
        id: "trade",
        name: "Fixed Trade Report",
        path: "fixed/trade",
        pathParams: {
            groupingType: "PurchasesSales",
            startDate: { dateSelectionType: "Today", asOfDate: null },
            endDate: { dateSelectionType: "Latest", asOfDate: null },
        },
        file: true,
    },
    {
        id: "taxlots",
        name: "Fixed Tax Lot Report",
        path: "fixed/taxlots",
        pathParams: {
            groupingType: "Open",
        },
        file: true,
    },
    {
        id: "cptycommission",
        name: "Fixed Commission Counterparty Report",
        path: "fixed/cptycommission",
        pathParams: {
            groupingType: "Open",
        },
        file: true,
    },
    {
        id: "position",
        name: "Fixed Position Report",
        path: "fixed/position",
        pathParams: {
            groupingType: "PnL",
        },
        file: true,
    },
];
export const ReportSelect = ({ value, onSelect, iconContainer, menuOpen = false, setMenuOpen = () => null, showFixedReports = false, showMyReports = false, showSharedReports = false, ...rest }) => {
    const { hasPerm } = useAuth();
    const [searchText, setSearchText] = React.useState("");
    const [term, setTerm] = React.useState(getFileName(value));
    const [key, setKey] = React.useState((value?.key ?? ""));
    const [options, setOptions] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const ref = React.useRef(null);
    const signalRef = React.useRef(new AbortController());
    const getOptions = React.useMemo(() => debounce(async (newTerm) => {
        setLoading(true);
        signalRef.current.abort();
        signalRef.current = new AbortController();
        const userTerms = showMyReports
            ? await getSearchRootNode("reports", ROOT_USER, newTerm, signalRef.current)
            : [];
        const sharedTerms = showSharedReports
            ? await getSearchRootNode("reports", ROOT_SHARED, newTerm, signalRef.current)
            : [];
        const globalTerms = hasPerm("DashboardEditor")
            ? await getSearchRootNode("reports", ROOT_GLOBAL, newTerm, signalRef.current)
            : [];
        setOptions([
            createRootFolder("Fixed Reports", showFixedReports ? fixedReportList : [], ROOT_FIXED),
            createRootFolder("My Reports", userTerms, ROOT_USER),
            createRootFolder("Shared Reports", sharedTerms, ROOT_SHARED),
            createRootFolder("Template Reports", globalTerms, ROOT_GLOBAL),
        ].filter((i) => i !== null));
        setLoading(false);
    }, 400), [showFixedReports, showMyReports, showSharedReports]);
    const setReportList = async () => {
        setLoading(true);
        try {
            const path = "/internal/api/documents/v2/reports";
            signalRef.current.abort();
            signalRef.current = new AbortController();
            const userReportList = showMyReports
                ? await getRootNode(path, ROOT_USER, signalRef.current)
                : [];
            const sharedReportList = showSharedReports
                ? await getRootNode(path, ROOT_SHARED, signalRef.current)
                : [];
            const globalReportList = hasPerm("DashboardEditor")
                ? await getRootNode(path, ROOT_GLOBAL, signalRef.current)
                : [];
            setOptions([
                createRootFolder("Fixed Reports", showFixedReports ? fixedReportList : [], ROOT_FIXED),
                createRootFolder("My Reports", userReportList, ROOT_USER),
                createRootFolder("Shared Reports", sharedReportList, ROOT_SHARED),
                createRootFolder("Template Reports", globalReportList, ROOT_GLOBAL),
            ].filter((i) => i !== null));
        }
        catch (error) {
            AppLogging.safeError("Failed to get report nodes", error);
        }
        finally {
            setLoading(false);
        }
    };
    const selected = React.useMemo(() => {
        if (value)
            return [value.id];
    }, [value]);
    const handleInputChange = (e) => {
        const newTerm = e.target.value;
        setSearchText(newTerm);
        getOptions(newTerm);
    };
    const handleClear = () => {
        if (menuOpen) {
            setSearchText("");
            setReportList();
        }
        else {
            setTerm("");
            onSelect?.(null);
        }
    };
    const handleInputFocus = () => {
        setMenuOpen(true);
    };
    React.useEffect(() => {
        if (menuOpen) {
            if (!searchText) {
                setReportList();
            }
            else {
                getOptions(searchText);
            }
        }
    }, [menuOpen, showFixedReports]);
    const closeMenu = React.useCallback(() => {
        setMenuOpen(false);
        setOptions([]);
    }, []);
    const handleClick = useRefCallback((node) => {
        if (node.file) {
            setTerm(getFileName(node));
            setSearchText("");
            closeMenu();
            onSelect?.(node, key);
        }
    }, [onSelect, closeMenu, key]);
    const handleKeyChangeCallback = useRefCallback(debounce((key) => {
        if (value)
            onSelect?.(value, key);
    }, 400), [value, onSelect]);
    const handleKeyChange = useRefCallback((e) => {
        const newKey = e.target.value;
        setKey(newKey);
        handleKeyChangeCallback(newKey);
    }, [value]);
    return (React.createElement(SingleRowContainer, null,
        React.createElement(TextInput, { ref: ref, value: menuOpen ? searchText : term, onChange: handleInputChange, iconContainer: iconContainer, onFocus: handleInputFocus, onClearValue: handleClear, clearable: !menuOpen, ...rest }),
        React.createElement(TextInput, { label: "Optional Key", value: key, onChange: handleKeyChange, "data-e2e-id": "optional-key" }),
        React.createElement(Portal, { open: menuOpen, attachedRef: ref, onClickOutside: closeMenu },
            React.createElement(OptionsContainer, null,
                loading ? (React.createElement(CenterContent, { style: { minHeight: 30 } },
                    React.createElement(Spinner, { size: "lg" }))) : null,
                !loading ? (options.length > 0 ? (React.createElement(FileExplorer, { nodes: options, getFileIcon: getFileIcon, getFileName: getFileName, onEntryClick: handleClick, retrieveNodes: getNodes, multiSelect: false, values: selected })) : (React.createElement(CenterContent, { style: { minHeight: 30 } }, "No options found"))) : null))));
};
